/**
 * The signed-in session's token.
 *
 * The token lives in the "key" cookie, which ~190 components read directly. A
 * browser can drop that cookie while the page stays open (an extension, a
 * cleaner, the user) even though the server still accepts the token. Every new
 * request then went out as "Bearer undefined": an employee could not log out,
 * and no check-out was recorded for her day.
 *
 * So the page also remembers the token it last saw, and getSessionToken() puts
 * the cookie back if it disappears mid-session. It never revives a session
 * that ended on purpose: see the three conditions there.
 */
import Cookies from "js-cookie";

const COOKIE = "key";
const ROLE = "userRole";
const ENDS_AT = "sessionExpiresAt";

/** When the login on record is meant to end (0 if none). Unique to that login. */
const storedEndsAt = () => Number(localStorage.getItem(ENDS_AT)) || 0;

/** The cookie's token, paired with the login it belongs to. */
function current() {
  const token = Cookies.get(COOKIE);
  return token ? { token, endsAt: storedEndsAt() } : undefined;
}

let remembered = current();

/** Sign in: the cookie, the role, and when this session is meant to end. */
export function startSession(token: string, role: string, hours: number) {
  const endsAt = Date.now() + hours * 60 * 60 * 1000;
  Cookies.set(COOKIE, token, { expires: new Date(endsAt) });
  localStorage.setItem(ROLE, role);
  localStorage.setItem(ENDS_AT, String(endsAt));
  remembered = { token, endsAt };
}

/** The token to send, restoring the cookie if it went missing mid-session. */
export function getSessionToken(): string | undefined {
  const present = current();
  if (present) {
    remembered = present;
    return present.token;
  }

  // The cookie is gone. Put it back only if the login it came from is still:
  //  - signed in: every logout in the app, in any tab, removes the stored role;
  //  - the one on record: a tab left over from an earlier login must not hand
  //    its token to whoever signed in after it;
  //  - not yet over: once its time is up the cookie is meant to be gone.
  const endsAt = storedEndsAt();
  if (
    !remembered ||
    !localStorage.getItem(ROLE) ||
    remembered.endsAt !== endsAt ||
    endsAt <= Date.now()
  ) {
    remembered = undefined;
    return undefined;
  }

  // Same token, and only the lifetime it had left.
  Cookies.set(COOKIE, remembered.token, { expires: new Date(endsAt) });
  return remembered.token;
}

/** Forget the session in this browser: the cookie, the role, the remembered copy. */
export function endSession() {
  Cookies.remove(COOKIE);
  localStorage.removeItem(ROLE);
  localStorage.removeItem(ENDS_AT);
  remembered = undefined;
}
